import { TONCENTER_API_KEY, TONCENTER_WEBSOCKET_ENDPOINT } from '~/config';
import { generateMessage } from '~/api/toncenterV3';
import store from '../store.js';

let ws;
let isOpen = false;
const messageQueue = [];
let reconnectTimeout = null;
let subscriptions = [];
let heartbeatInterval = null;

function sendWhenReady(data) {
    const payload = JSON.stringify(data);

    if (isOpen) {
        ws.send(payload);
    } else {
        messageQueue.push(payload);
    }
}

function flushQueue() {
    while (messageQueue.length > 0 && isOpen) {
        ws.send(messageQueue.shift());
    }
}

function stopHeartbeat() {
    if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
        heartbeatInterval = null;
    }
}

function startHeartbeat() {
    stopHeartbeat(); // на всякий случай сбрасываем старый

    heartbeatInterval = setInterval(() => {
        sendWhenReady({ operation: 'ping' });
    }, 7000); // 7 секунд – оптимально
}

function formatActions(addr, actionsRaw, address_book, metadata, finality) {
    const actions = [];

    actionsRaw.forEach((action) => {
        const findByHash = actions.findIndex(({ hash }) => hash === action.trace_external_hash_norm);

        const msg = generateMessage(action, address_book, addr, metadata, finality);

        msg.hash = action.trace_external_hash_norm;

        if (msg.event === 'dex_withdraw_liquidity' || msg.event === 'dex_deposit_liquidity') {
            const meta_1 = {
                symbol: msg.details.symbol_1,
                jetton_address: msg.details.jetton_1_address,
                decimals: msg.details.decimals_1,
                amount: msg.details.amount_1,
            };
            const meta_2 = {
                symbol: msg.details.symbol_2,
                jetton_address: msg.details.jetton_2_address,
                decimals: msg.details.decimals_2,
                amount: msg.details.amount_2,
            };
            const action_1 = {
                action: {
                    type: 'dex:deposit_liquidity_asset_1',
                },
            };
            const action_2 = {
                action: {
                    type: 'dex:deposit_liquidity_asset_2',
                },
            };
            actions.push({
                address: addr,
                fee: null,
                hash: action.trace_external_hash_norm,
                lt: action.end_lt,
                trace_end_lt: action.trace_end_lt,
                timestamp: action.end_utime * 1000,
                messages: [{ ...msg, ...action_1, meta: meta_1 }, { ...msg, ...action_2, meta: meta_2 }],
                action: 'ok',
                finality,
            });
            return;
        }

        if (findByHash === -1) {
            actions.push({
                address: addr,
                fee: null,
                hash: action.trace_external_hash_norm,
                lt: action.end_lt,
                trace_end_lt: action.trace_end_lt,
                timestamp: action.end_utime * 1000,
                messages: [msg],
                action: 'ok',
                finality,
            });
        } else {
            actions[findByHash].messages.push(msg);
        }
    });

    return actions;
}

function establishWebsocketConnection() {
    if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
    }

    ws = new WebSocket(`${TONCENTER_WEBSOCKET_ENDPOINT}?api_key=${TONCENTER_API_KEY}`);

    ws.onopen = () => {
        isOpen = true;
        // console.log('connected');

        // restore subscriptions after reconnect
        // eslint-disable-next-line no-restricted-syntax
        for (const sub of subscriptions) {
            sendWhenReady(sub);
        }

        // Make ping request so connection will not close
        startHeartbeat();

        flushQueue();
    };

    ws.onmessage = (msg) => {
        const data = JSON.parse(msg.data);
        const account = data.id;

        // console.log('new action:', data);

        if (data?.type === 'account_state_change') {
            if (data?.finality === 'finalized') {
                store.state.websocketAccountState = data?.state;
            }
        } else if (data?.type === 'actions') {
            const formattedActions = formatActions(subscriptions[0].addresses[0], data?.actions, data?.address_book, data?.metadata, data?.finality);

            if (data?.finality === 'finalized') {
                store.state.websocketPendingTransactions = store.state.websocketPendingTransactions
                    .filter(item => item.hash !== data?.trace_external_hash_norm);

                store.state.websocketTransactions = store.state.websocketTransactions
                    .filter(item => item.hash !== data?.trace_external_hash_norm);

                store.state.websocketTransactions.unshift(...formattedActions);
            } else {
                store.state.websocketPendingTransactions = store.state.websocketPendingTransactions
                    .filter(item => item.hash !== data?.trace_external_hash_norm);

                store.state.websocketPendingTransactions.unshift(...formattedActions);
            }
        } else if (data?.type === 'trace_invalidated') {
            store.state.websocketPendingTransactions = store.state.websocketPendingTransactions
                .filter(item => item.hash !== data?.trace_external_hash_norm);

            store.state.websocketTransactions = store.state.websocketTransactions
                .filter(item => item.hash !== data?.trace_external_hash_norm);
        }
    };

    ws.onerror = (err) => {
        console.error('ws error', err);
        stopHeartbeat();
    };

    ws.onclose = () => {
        isOpen = false;
        // console.log('ws closed, reconnecting...');
        stopHeartbeat();

        reconnectTimeout = setTimeout(() => {
            establishWebsocketConnection();
        }, 1000);
    };
}

export function startTxAndStateWebsocket(address) {
    const params = {
        id: address,
        operation: 'subscribe',
        addresses: [address],
        types: ['actions', 'account_state_change'],
        min_finality: 'pending',
        include_address_book: true,
        include_metadata: true,
    };

    subscriptions.push(params);

    if (!ws || ws.readyState === WebSocket.CLOSED) {
        establishWebsocketConnection();
    } else {
        sendWhenReady(params);
    }
}

export function stopTxAndStateWebsockets() {
    if (!ws || ws.readyState === WebSocket.CLOSED) return;

    const addresses = subscriptions.map(subscription => subscription.addresses[0]);

    const params = {
        operation: 'unsubscribe',
        addresses,
    };

    if (addresses.length) {
        // console.log('UNSUBSCRIBE from addresses:', addresses);
        sendWhenReady(params);
        subscriptions = [];
        store.state.websocketAccountState = null;
        store.state.websocketTransactions = [];
        store.state.websocketPendingTransactions = [];
    }
}

export function closeWebsocketConnection() {
    stopTxAndStateWebsockets();
    ws.close();
}
